import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

import analyticsImg from "../../../../Assets/Images/whatsapp/analytics.png";

const analyticsFeatures = [
  {
    title: "Delivery & Read Reports",
    desc: "Track message delivery, read receipts and customer engagement in real time.",
  },
  {
    title: "Campaign Performance",
    desc: "Measure campaign reach, replies, conversions and click performance with detailed reports.",
  },
  {
    title: "Agent Productivity",
    desc: "Monitor response time, team workload and conversation efficiency from one dashboard.",
  },
  {
    title: "Customer Insights",
    desc: "Understand customer behaviour and communication trends to improve engagement.",
  },
];

const stats = [
  {
    number: "99.9%",
    label: "Platform Uptime",
  },
  {
    number: "50K+",
    label: "Daily Messages",
  },
  {
    number: "250+",
    label: "Business Clients",
  },
  {
    number: "24/7",
    label: "Technical Support",
  },
];

export default function FeaturesSection() {
  return (
    <section
      className="
        relative
        overflow-hidden
        theme-bg-primary
        theme-text-primary
        py-12
        font-saira
        transition-colors
        duration-500

        min-[375px]:py-14
        sm:py-16
        md:py-18
        lg:py-20
      "
    >
      {/* ============================================================
          BACKGROUND GLOW
          GREEN — SAME IN BOTH THEMES
      ============================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          left-[-100px]
          top-32
          h-64
          w-64
          rounded-full
          bg-green-500/10
          blur-[140px]

          min-[375px]:h-72
          min-[375px]:w-72

          sm:h-80
          sm:w-80
          sm:blur-[160px]

          lg:left-0
          lg:h-80
          lg:w-80
          lg:blur-[170px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          right-[-100px]
          bottom-0
          h-64
          w-64
          rounded-full
          bg-green-500/10
          blur-[140px]

          min-[375px]:h-72
          min-[375px]:w-72

          sm:h-80
          sm:w-80
          sm:blur-[160px]

          lg:right-0
          lg:h-80
          lg:w-80
          lg:blur-[170px]
        "
      />

      {/* ============================================================
          MAIN CONTAINER
      ============================================================ */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4

          min-[375px]:px-5
          sm:px-6
          lg:px-8
        "
      >
        {/* ============================================================
            MAIN GRID
        ============================================================ */}

        <div
          className="
            grid
            grid-cols-1
            items-stretch
            gap-10

            min-[375px]:gap-12
            sm:gap-14
            md:gap-16

            lg:grid-cols-2
            lg:gap-16
          "
        >
          {/* ==========================================================
              LEFT CONTENT
          ========================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: -60,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.8,
            }}
            className="
              flex
              min-w-0
              flex-col
              justify-between
              text-center

              lg:text-left
            "
          >
            {/* ========================================================
                HEADING CONTENT
            ======================================================== */}

            <div>
              <span
                className="
                  inline-block
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[2.5px]
                  text-green-400

                  min-[375px]:text-[11px]
                  min-[375px]:tracking-[3px]

                  sm:text-xs
                  sm:tracking-[3.5px]

                  md:text-sm
                  md:tracking-[4px]

                  lg:text-sm
                  lg:tracking-[4px]
                "
              >
                Analytics & Insights
              </span>

              <h2
                className="
                  mt-3
                  text-[29px]
                  font-bold
                  leading-[1.15]
                  theme-text-primary

                  min-[375px]:text-[31px]

                  sm:mt-4
                  sm:text-4xl

                  md:text-[42px]

                  lg:mt-5
                  lg:text-5xl
                  lg:leading-tight
                "
              >
                Unlock Powerful
                <br />
                <span className="text-green-400">
                  Analytics & Insights
                </span>
              </h2>

              <p
                className="
                  mx-auto
                  mt-4
                  max-w-2xl
                  text-[13px]
                  leading-6
                  theme-text-muted

                  min-[375px]:text-sm
                  min-[375px]:leading-6

                  sm:mt-5
                  sm:text-base
                  sm:leading-7

                  lg:mx-0
                  lg:mt-4
                  lg:max-w-none
                "
              >
                Gain complete visibility into customer conversations, campaign
                performance, delivery reports and team productivity through a
                unified analytics dashboard designed for growing businesses.
              </p>
            </div>

            {/* ========================================================
                FEATURE LIST
            ======================================================== */}

            <div
              className="
                mt-7
                space-y-5

                min-[375px]:mt-8
                min-[375px]:space-y-6

                sm:mt-9
                sm:space-y-7

                lg:mt-6
                lg:space-y-6
              "
            >
              {analyticsFeatures.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  whileInView={{
                    opacity: 1,
                    x: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.12,
                  }}
                  whileHover={{
                    x: 6,
                  }}
                  className="
                    flex
                    min-w-0
                    items-start
                    justify-center
                    gap-3
                    text-center

                    min-[375px]:gap-4
                    sm:gap-4

                    lg:justify-start
                    lg:text-left
                  "
                >
                  {/* Icon */}

                  <div
                    className="
                      mt-1
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-green-500/15

                      min-[375px]:h-10
                      min-[375px]:w-10

                      sm:h-11
                      sm:w-11
                    "
                  >
                    <CheckCircle2
                      className="
                        h-[17px]
                        w-[17px]
                        text-green-400

                        sm:h-5
                        sm:w-5
                      "
                    />
                  </div>

                  {/* Text */}

                  <div
                    className="
                      min-w-0
                      max-w-[520px]
                    "
                  >
                    <h4
                      className="
                        text-[16px]
                        font-semibold
                        leading-5
                        theme-text-primary

                        min-[375px]:text-[17px]

                        sm:text-lg

                        md:text-xl
                      "
                    >
                      {item.title}
                    </h4>

                    <p
                      className="
                        mt-1.5
                        text-[11px]
                        leading-5
                        theme-text-muted

                        min-[375px]:text-xs
                        min-[375px]:leading-5

                        sm:mt-2
                        sm:text-sm
                        sm:leading-6

                        lg:leading-7
                      "
                    >
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ==========================================================
              RIGHT IMAGE
          ========================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: 60,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.8,
            }}
            className="
              flex
              h-full
              min-w-0
              items-center
            "
          >
            <div
              className="
                relative
                w-full
                min-w-0
              "
            >
              {/* ======================================================
                  DASHBOARD IMAGE CONTAINER
              ====================================================== */}

              <div
                className="
                  relative
                  w-full
                  overflow-hidden
                  rounded-2xl
                  theme-border
                  theme-bg-secondary
                  shadow-[0_0_40px_rgba(37,211,102,0.10)]

                  min-[375px]:rounded-[20px]

                  sm:rounded-[22px]

                  md:rounded-[24px]

                  lg:rounded-[24px]
                  lg:shadow-[0_0_50px_rgba(37,211,102,0.12)]
                "
              >
                <img
                  src={analyticsImg}
                  alt="Analytics Dashboard"
                  loading="lazy"
                  className="
                    block
                    h-[320px]
                    w-full
                    object-cover
                    object-center

                    max-[374px]:h-[280px]

                    min-[375px]:h-[330px]

                    sm:h-[400px]

                    md:h-[460px]

                    lg:h-[600px]
                  "
                />

                {/* ====================================================
                    IMAGE OVERLAY
                    KEEP BLACK — PART OF IMAGE DESIGN
                ==================================================== */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-gradient-to-t
                    from-black/10
                    via-transparent
                    to-transparent
                  "
                />
              </div>

              {/* ======================================================
                  FLOATING KPI — DELIVERY RATE
              ====================================================== */}

              <motion.div
                animate={{
                  y: [0, -6, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
                  absolute
                  right-2
                  top-3
                  rounded-xl
                  border
                  border-green-500/20
                  theme-card
                  px-2.5
                  py-2
                  shadow-lg
                  backdrop-blur-xl

                  max-[374px]:right-1.5
                  max-[374px]:top-2
                  max-[374px]:px-2
                  max-[374px]:py-1.5

                  min-[375px]:right-2.5
                  min-[375px]:top-3

                  sm:right-4
                  sm:top-4
                  sm:px-3.5
                  sm:py-2.5

                  md:right-5
                  md:top-5

                  lg:right-6
                  lg:top-6
                  lg:px-4
                  lg:py-3
                "
              >
                <p
                  className="
                    text-[7px]
                    uppercase
                    tracking-[1.2px]
                    theme-text-muted

                    min-[375px]:text-[8px]

                    sm:text-[9px]
                    sm:tracking-[1.5px]

                    lg:text-[10px]
                    lg:tracking-[2px]
                  "
                >
                  Delivery Rate
                </p>

                <h3
                  className="
                    mt-1
                    text-lg
                    font-bold
                    leading-none
                    text-green-400

                    min-[375px]:text-xl

                    sm:text-2xl
                  "
                >
                  98.7%
                </h3>

                <p
                  className="
                    mt-1
                    text-[8px]
                    theme-text-muted

                    min-[375px]:text-[9px]

                    sm:text-xs
                  "
                >
                  Success Rate
                </p>
              </motion.div>

              {/* ======================================================
                  FLOATING MESSAGE CARD
              ====================================================== */}

              <motion.div
                animate={{
                  y: [0, 6, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="
                  absolute
                  bottom-3
                  left-2
                  rounded-xl
                  border
                  theme-border
                  theme-card
                  px-3
                  py-2.5
                  shadow-[0_10px_35px_var(--shadow-color)]
                  backdrop-blur-xl

                  max-[374px]:bottom-2
                  max-[374px]:left-1.5
                  max-[374px]:px-2
                  max-[374px]:py-2

                  min-[375px]:bottom-3
                  min-[375px]:left-2.5

                  sm:bottom-4
                  sm:left-4
                  sm:px-4
                  sm:py-3

                  md:bottom-5
                  md:left-5

                  lg:bottom-6
                  lg:left-6
                  lg:px-5
                  lg:py-3
                "
              >
                <div
                  className="
                    flex
                    items-center
                    gap-2

                    sm:gap-3
                  "
                >
                  <div
                    className="
                      flex
                      h-7
                      w-7
                      shrink-0
                      items-center
                      justify-center
                      rounded-lg
                      bg-green-500/15

                      sm:h-8
                      sm:w-8
                      sm:rounded-xl

                      lg:h-9
                      lg:w-9
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="
                        h-4
                        w-4
                        text-green-400

                        sm:h-[18px]
                        sm:w-[18px]

                        lg:h-5
                        lg:w-5
                      "
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 17v-6m4 6V7m4 10v-3"
                      />
                    </svg>
                  </div>

                  <div>
                    <p
                      className="
                        text-[7px]
                        uppercase
                        tracking-wider
                        theme-text-muted

                        min-[375px]:text-[8px]

                        sm:text-[9px]

                        lg:text-[10px]
                      "
                    >
                      Messages
                    </p>

                    <h3
                      className="
                        mt-0.5
                        text-lg
                        font-bold
                        leading-none
                        text-green-400

                        min-[375px]:text-xl

                        sm:text-2xl
                      "
                    >
                      1.2M+
                    </h3>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* ============================================================
            STATS
        ============================================================ */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            delay: 0.2,
          }}
          className="
            mt-12
            grid
            grid-cols-2
            gap-x-5
            gap-y-8
            border-t
            theme-border
            pt-8
            text-center

            min-[375px]:mt-14
            min-[375px]:gap-x-6
            min-[375px]:gap-y-9
            min-[375px]:pt-9

            sm:mt-16
            sm:gap-x-8
            sm:gap-y-10
            sm:pt-10

            md:grid-cols-4
            md:gap-8

            lg:mt-20
            lg:grid-cols-4
            lg:gap-10
            lg:pt-10
          "
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                delay: index * 0.1,
              }}
              whileHover={{
                y: -5,
              }}
              className="
                min-w-0
                text-center
              "
            >
              <h3
                className="
                  text-[28px]
                  font-bold
                  leading-none
                  text-green-400

                  min-[375px]:text-[30px]

                  sm:text-4xl

                  md:text-4xl

                  lg:text-5xl
                "
              >
                {stat.number}
              </h3>

              <p
                className="
                  mx-auto
                  mt-2
                  max-w-[130px]
                  text-[9px]
                  leading-4
                  theme-text-muted

                  min-[375px]:text-[10px]

                  sm:mt-2
                  sm:max-w-none
                  sm:text-xs

                  lg:mt-3
                  lg:text-sm
                "
              >
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}